// Single source of truth for procurement pipeline stages → DS status tones + labels.
// Pure TypeScript — no React. Every procurement status chip imports from here.
// Source: /Users/joemeadows/Desktop/Tenon/Design/Tenon-9/procurement/procurement-data.jsx

export type PipelineKind = 'material' | 'service'

export type StatusTone = 'not_started' | 'in_progress' | 'blocked' | 'completed' | 'on_hold' | 'cancelled'

// Canonical pipeline stage arrays (source: procurement-data.jsx lines 54-55)
export const MAT_PIPELINE = [
  'needs_ordering', 'bid_request', 'quoted', 'po_issued', 'ordered', 'in_transit', 'received',
] as const
export type MatStage = typeof MAT_PIPELINE[number]

export const SVC_PIPELINE = [
  'needs_ordering', 'bid_request', 'quoted', 'awarded', 'booked', 'in_progress', 'completed',
] as const
export type SvcStage = typeof SVC_PIPELINE[number]

// Stage → DS tone (source: procurement-data.jsx line 21)
const STAGE_TO_TONE: Record<string, StatusTone> = {
  needs_ordering: 'not_started',
  bid_request:    'in_progress',
  quoted:         'in_progress',
  po_issued:      'in_progress',
  ordered:        'in_progress',
  in_transit:     'in_progress',
  received:       'completed',
  awarded:        'in_progress',
  booked:         'in_progress',
  in_progress:    'in_progress',
  completed:      'completed',
  cancelled:      'cancelled',
  on_hold:        'on_hold',
}

// Stage → display label (source: procurement-data.jsx line 37)
const STAGE_LABEL: Record<string, string> = {
  needs_ordering: 'Needs ordering',
  bid_request:    'Bid sent',
  quoted:         'Quoted',
  po_issued:      'PO issued',
  ordered:        'Ordered',
  in_transit:     'In transit',
  received:       'Received',
  awarded:        'Awarded',
  booked:         'Booked',
  in_progress:    'In progress',
  completed:      'Completed',
  cancelled:      'Cancelled',
  on_hold:        'On hold',
}

export function pipelineTone(stage: string, _kind: PipelineKind): StatusTone {
  return STAGE_TO_TONE[stage] ?? 'not_started'
}

export function pipelineLabel(stage: string, _kind: PipelineKind): string {
  return STAGE_LABEL[stage] ?? stage
}

// ─── Bid request status ────────────────────────────────────────────────────
// Source: procurement-data.jsx lines 161 + 170
export const BID_STATUS_TONE: Record<string, StatusTone> = {
  // design-spec display values (mockup)
  draft:     'not_started',
  open:      'in_progress',
  quoted:    'in_progress',
  closing:   'on_hold',
  awarded:   'completed',
  closed:    'cancelled',
  cancelled: 'cancelled',
  // API backend values
  sent:     'in_progress',
  invited:  'in_progress',
  received: 'in_progress',
  declined: 'cancelled',
}
export const BID_STATUS_LABEL: Record<string, string> = {
  // design-spec labels
  draft: 'Draft', open: 'Open', quoted: 'Quoted', closing: 'Closing',
  awarded: 'Awarded', closed: 'Closed', cancelled: 'Cancelled',
  // API backend labels
  sent:     'Open',
  invited:  'Open',
  received: 'Responses in',
  declined: 'Declined',
}
export function bidTone(status: string): StatusTone { return BID_STATUS_TONE[status] ?? 'not_started' }
export function bidLabel(status: string): string { return BID_STATUS_LABEL[status] ?? status }

// ─── Subcontract status ────────────────────────────────────────────────────
// Source: procurement-data.jsx lines 189 + 193
export const SUB_STATUS_TONE: Record<string, StatusTone> = {
  draft:       'not_started',
  sent:        'in_progress',
  signed:      'in_progress',
  in_progress: 'in_progress',
  completed:   'completed',
  cancelled:   'cancelled',
}
export const SUB_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', sent: 'Sent', signed: 'Signed',
  in_progress: 'In progress', completed: 'Completed', cancelled: 'Cancelled',
}
export function subTone(status: string): StatusTone { return SUB_STATUS_TONE[status] ?? 'not_started' }
export function subLabel(status: string): string { return SUB_STATUS_LABEL[status] ?? status }

// ─── Purchase order status ─────────────────────────────────────────────────
// Source: procurement-data.jsx lines 216 + 221
export const PO_STATUS_TONE: Record<string, StatusTone> = {
  draft:            'not_started',
  sent:             'in_progress',
  acknowledged:     'in_progress',
  approved:         'in_progress',
  in_transit:       'in_progress',
  shipped:          'in_progress',
  partial_received: 'in_progress',
  // partially_received: the DB vocabulary value (UOW-5D §3.15, migration
  // 00900) — any line received but not all, flipped by the receipt flow.
  // partial_received above is the older design-spec spelling, kept for the
  // mockup data path.
  partially_received: 'in_progress',
  received:         'completed',
  closed:           'completed',
  cancelled:        'cancelled',
}
export const PO_STATUS_LABEL: Record<string, string> = {
  draft: 'Draft', sent: 'Sent', acknowledged: 'Acknowledged',
  approved: 'Approved', shipped: 'Shipped',
  in_transit: 'In transit', partial_received: 'Partial received',
  partially_received: 'Partially received',
  received: 'Received', closed: 'Closed', cancelled: 'Cancelled',
}
export function poTone(status: string): StatusTone { return PO_STATUS_TONE[status] ?? 'not_started' }
export function poLabel(status: string): string { return PO_STATUS_LABEL[status] ?? status }

// ─── Shipment status ───────────────────────────────────────────────────────
// Source: procurement-data.jsx lines 239 + 246
// DB-side status values (awaiting_shipment, shipped, in_transit, delivered, verified, exception)
// are merged here alongside spec-side values (dispatched, out_for_delivery).
export const SHIP_STATUS_TONE: Record<string, StatusTone> = {
  // spec-canonical
  dispatched:        'not_started',
  in_transit:        'in_progress',
  out_for_delivery:  'in_progress',
  delivered:         'completed',
  exception:         'blocked',
  // DB-side aliases
  awaiting_shipment: 'not_started',
  shipped:           'not_started',
  verified:          'completed',
}
export const SHIP_STATUS_LABEL: Record<string, string> = {
  dispatched:        'Dispatched',
  in_transit:        'In transit',
  out_for_delivery:  'Out for delivery',
  delivered:         'Delivered',
  exception:         'Exception',
  awaiting_shipment: 'Dispatched',
  shipped:           'Dispatched',
  verified:          'Verified',
}
export function shipTone(status: string): StatusTone { return SHIP_STATUS_TONE[status] ?? 'not_started' }
export function shipLabel(status: string): string { return SHIP_STATUS_LABEL[status] ?? status }

// ── At-risk reason vocabulary (UOW-5B, §3.13) ────────────────────────────────
//
// THE wording for every at-risk pill, everywhere: the procurement tracker, the
// task peek's Sourcing facet and its task-level rollup, and the §3.11
// related-work cards. §3.13's design contract is explicit — "pill + reason
// vocabulary consistent across tracker, facet, cards" — and three surfaces
// each holding their own copy is how that stops being true one rename at a
// time. Mirrors atrisk.Label in internal/atrisk, which serves the prose
// surfaces (the Sage digest line, the MCP tool summary) on the server side.
//
// The reason VALUES are produced by the server and are never computed here:
// at-risk is derived per read from one SQL definition and delivered on the
// row. This map only decides how to say it.
export const AT_RISK_REASON_LABEL: Record<string, string> = {
  missed: 'Need-by date passed',
  promise_after_need: 'Vendor promise lands after need-by',
  sub_unconfirmed: 'Subcontractor not confirmed',
  order_window_passed: 'Order window passed',
}

/** Human label for an at-risk reason. An unrecognised value passes through
 *  rather than rendering blank — a reason this build has not heard of (a newer
 *  server, or the 5C seam once it wires up) should read as itself, never as an
 *  empty pill. */
export function atRiskReasonLabel(reason?: string | null): string {
  if (!reason) return 'At risk'
  return AT_RISK_REASON_LABEL[reason] ?? reason
}
